import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface StripeConnectStatus {
  hasAccount: boolean;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted?: boolean;
  loading: boolean;
  error: string | null;
}

interface StripeConnectResponse {
  hasAccount?: boolean;
  onboardingComplete?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  url?: string;
  error?: string;
}

export function useStripeConnect() {
  const { user, session, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<StripeConnectStatus>({
    hasAccount: false,
    onboardingComplete: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    loading: true,
    error: null,
  });

  const callStripeFunction = useCallback(async (functionName: string) => {
    const accessToken = session?.access_token ?? (await supabase.auth.getSession()).data.session?.access_token;

    if (!accessToken) {
      throw new Error("Please log in again to continue");
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    const data = (await response.json().catch(() => ({}))) as StripeConnectResponse;

    if (!response.ok) {
      throw new Error(data.error || `Request failed with status ${response.status}`);
    }

    return data;
  }, [session?.access_token]);

  const checkStatus = useCallback(async () => {
    if (authLoading) return null;

    if (!user) {
      setStatus((prev) => ({ ...prev, loading: false }));
      return null;
    }

    try {
      setStatus((prev) => ({ ...prev, loading: true, error: null }));

      const data = await callStripeFunction("stripe-connect-status");

      const next = {
        hasAccount: data.hasAccount || false,
        onboardingComplete: data.onboardingComplete || false,
        chargesEnabled: data.chargesEnabled || false,
        payoutsEnabled: data.payoutsEnabled || false,
        detailsSubmitted: data.detailsSubmitted,
        loading: false,
        error: null,
      };
      setStatus(next);
      return next;
    } catch (error: any) {
      console.error("Error checking Stripe Connect status:", error);
      setStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to check payment setup status",
      }));
      return null;
    }
  }, [authLoading, callStripeFunction, user]);


  const startOnboarding = useCallback(async (): Promise<string | null> => {
    if (authLoading || !user) return null;

    try {
      const data = await callStripeFunction("stripe-connect-onboarding");

      if (data.onboardingComplete) {
        await checkStatus();
        return null;
      }

      return data.url || null;
    } catch (error: any) {
      console.error("Error starting Stripe onboarding:", error);
      throw error;
    }
  }, [authLoading, callStripeFunction, checkStatus, user]);

  useEffect(() => {
    if (!authLoading) {
      void checkStatus();
    }
  }, [authLoading, checkStatus]);

  return {
    ...status,
    checkStatus,
    startOnboarding,
  };
}
